import { toKebabCase } from "@eusoft/webapp-core/utils/String";
import { fixEndDate, fixStartDate, formatCurrency, formatString, formatText, objectEditor, parseDate, required, staticItemsSource } from "@eusoft/webapp-ui";
import { contentInfo } from "@eusoft/webapp-ui/abstraction/IContent";
import { MaterialIcon } from "@eusoft/webapp-ui/components/Icon";
import { chartDatePart, formatDatePart, ReportViewContent, type IReportViewOptions } from "./ReportViewContent";
import { OrderType, PaymentMethod, SalePointType, TimeUnit, type IOrdersReport, type IOrdersReportRow } from "../../entities/Commands";
import { merchantItemsSource, productItemsSource, salePointsItemsSource } from "../../helpers/ItemsSources";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { executeAsync } from "../../services/DataService";
import { WithContext } from "../../services/WithContext";
import 'chartjs-adapter-date-fns';
import { groupBy } from "@eusoft/webapp-core/utils/Array";
import type { ChartType } from "chart.js";
import { registerComponent } from "@eusoft/webapp-core/Component";

interface IOrderGroup {
    id: unknown;
    name: string;
}

export class OrderReportPage extends ReportViewContent<IOrdersReport, IOrdersReportRow> {

    constructor() {

        super();

        this.init(OrderReportPage, {

            title: "order-report", 

            chartDimension: "total",

            rowFields: {

                "total": {
                    format: formatCurrency,
                },
                "count": {
                    label: formatText("order-count")
                },
            },

            prepareFilter: a => ({
                ...a,
                saleCircuitId: context.saleCircuit?.id,
                toDate: fixEndDate(a.toDate as Date),
                fromDate: fixStartDate(a.fromDate as Date),
            }),

            filterEditor: objectEditor<IOrdersReport>(bld => <>

                {bld.date(a => a.fromDate as Date, {
                    label: "from-date-art",

                })}

                {bld.date(a => a.toDate as Date, {
                    label: "to-date-art",
                })}


                {bld.singleSelector(a => a.orderType, {
                    validators: [required],
                    onChanged: (filter) => {
                        filter.salePointId = undefined;
                    },
                    editor: {
                        itemsSource: staticItemsSource(
                            ["buy", OrderType.Buy],
                            ["charge", OrderType.Charge],
                            ["refund", OrderType.CardRefund],
                        )
                    }
                })}

                {bld.autoComplete(a => a.merchantId, {
                    label: "merchant",
                    onChanged: (filter) => {
                        filter.salePointId = undefined;
                        filter.productId = undefined;
                    },
                    editor: {
                        freeText: false,
                        searchMode: "once",
                        prepareFilter: (f, q) => ({
                            ...f,
                            saleCircuitId: context.saleCircuit?.id
                        }),
                        itemsSource: merchantItemsSource
                    }
                })}

                {bld.autoComplete(a => a.salePointId, {
                    label: "sale-point",
                    editor: {
                        freeText: false,
                        searchMode: "once",
                        prepareFilter: (f, q) => ({
                            ...f,
                            saleCircuitId: context.saleCircuit?.id,
                            merchantId: bld.container.inputValue.merchantId,
                            type: [bld.container.inputValue.orderType == OrderType.Buy ? SalePointType.Sell : SalePointType.Charge]
                        }),
                        itemsSource: salePointsItemsSource
                    }
                })}


                {bld.autoComplete(a => a.productId, {
                    label: "product",
                    visible: a => a.orderType == OrderType.Buy,
  
                    editor: {
                        freeText: false,
                        searchMode: "once",
                        prepareFilter: (f, q) => ({
                            ...f,
                            saleCircuitId: context.saleCircuit?.id,
                            merchantId: bld.container.inputValue.merchantId,
                        }),
                        itemsSource: productItemsSource
                    }
                })}

                {bld.singleSelector(a => a.groupBy, {
                    editor: {
                        emptyItem: formatString("[no-group]"),
                        itemsSource: staticItemsSource(
                            ["sale-point", "SalePoint"],
                            ["merchant", "Merchant"],
                            ["product", "Product"],
                            ["payment-method", "PaymentMethod"],
                        )
                    }
                })}

                {bld.singleSelector(m => m.groupByTime, {
                    validators: [],
                    editor: {
                        emptyItem: formatString("[no-group]"),
                        valuesEquals: (a, b) => (JSON.stringify(a) == JSON.stringify(b)),
                        itemsSource: staticItemsSource(
                            ["15 {minutes}", { unit: TimeUnit.Minute, value: 15 }],
                            ["30 {minutes}", { unit: TimeUnit.Minute, value: 30 }],
                            ["1 {hour}", { unit: TimeUnit.Hour, value: 1 }],
                            ["6 {hours}", { unit: TimeUnit.Hour, value: 6 }],
                            ["1 {day}", { unit: TimeUnit.Day, value: 1 }],
                            ["1 {week}", { unit: TimeUnit.Week, value: 1 }],
                            ["1 {month}", { unit: TimeUnit.Month, value: 1 }],
                            ["1 {year}", { unit: TimeUnit.Year, value: 1 }]
                        )
                    }
                })}
            </>, {
                commitMode: "manual",
                style: "vertical",
                inputField: { style: "filled" },
            })
        } as IReportViewOptions<IOrdersReport, IOrdersReportRow>);
    }

    protected formatGroup(groupBy: string, group: IOrderGroup) {
        if (groupBy == "PaymentMethod")
            return formatString(toKebabCase(PaymentMethod[group.id as number]));
        return group.name ?? formatString("none");
    }

    override updateCharts(filter: IOrdersReport) {

        this.charts = [];

        if (!filter || !this.rows || !this.chartDimension)
            return;

        if (!filter.groupByTime?.value && !filter.groupBy)
            return false;

        if (!filter.groupByTime?.value) {

            if (filter.groupBy) {
                this.charts.push({
                    style: ["auto-height"],
                    config: {
                        type: 'pie',
                        data: {
                            labels: this.rows.map(row => this.formatGroup(filter.groupBy,
                                { name: row.groupName, id: row.groupId })),
                            datasets: [
                                {
                                    label: this.formatFieldLabel(this.chartDimension) as string,
                                    data: this.rows.map(row => row[this.chartDimension])
                                }
                            ]
                        }
                    },
                })
            }
        }
        else {
            if (!filter.groupBy) {
                for (const type of ["line", "bar"] as ChartType[]) {
                    this.charts.push({
                        config: {
                            type: type,
                            data: {

                                datasets: [
                                    {
                                        label: this.formatFieldLabel(this.chartDimension) as string,
                                        data: this.rows.map(row => ({
                                            y: row[this.chartDimension],
                                            x: parseDate(row.fromTime)
                                        })),
                                        tension: 0
                                    }
                                ]
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                    x: {
                                        type: "time",
                        
                                        time: {
                                            unit: chartDatePart(filter.groupByTime.unit)
                                        }
                                    }
                                }
                            }
                        }
                    })
                }

            }
            else {

                const timeGrps = groupBy(this.rows, a => ({
                    name: a.groupName,
                    id: a.groupId
                } as IOrderGroup));

                for (const type of ["line", "bar"] as ChartType[]) {
                    this.charts.push({
                        config: {
                            type: type,
                            data: {

                                datasets: timeGrps.map(grp => (
                                    {
                                        label: this.formatGroup(filter.groupBy, grp.key),
                                        data: grp.values.map(row => ({
                                            y: row[this.chartDimension],
                                            x: parseDate(row.fromTime)
                                        })),

                                        tension: 0
                                    }
                                ))
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                    x: {
                                        type: "time",
                       
                                        time: {
                                            unit: chartDatePart(filter.groupByTime.unit)
                                        }
                                    }
                                }
                            }
                        }
                    })
                }             
            }
        }

        return true;
    }

  

    override async queryAsync(filter: IOrdersReport) {

        if (filter.groupByTime?.value) {

            if (filter.groupBy) {
                this.groupBy = a => a.fromTime;
                this.formatGroupHeader = (a: Date) => formatDatePart(filter.groupByTime.unit, a);
                this.rowHeader = r => this.formatGroup(filter.groupBy, { name: r.groupName, id: r.groupId }) 
            }
            else {
                this.groupBy = a => "";
                this.rowHeader = r => formatDatePart(filter.groupByTime.unit, r.fromTime);
            }
        }
        else {
            this.rowHeader = r => this.formatGroup(filter.groupBy, { name: r.groupName, id: r.groupId }) 
            this.groupBy = null;
        }
  

        this.groupLabel = filter.groupBy ? formatText(toKebabCase(filter.groupBy)) : formatText("time-label");

        return (await executeAsync("OrdersReport", filter)).rows;
    }

    static override info = contentInfo({
        name: "order-report",
        route: "/reports/order",
        icon: <MaterialIcon color="#00BCD4" name="shopping_cart" />,
        features: [ 
            Authorize(a => a.businessAccount().resource("SaleCircuit", "admin")),
            WithContext("sale-circuit")
        ],
        factory: () => new OrderReportPage()
    });
}

registerComponent(OrderReportPage, "OrderReportPage");

